import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { BgColor } from 'tbk-components/src/components/Core/colors';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage, IImageProps } from 'tbk-components/src/components/Image';
import Video, { IVideoProps, IVideo } from 'tbk-components/src/components/Video';
import isMobile from 'tbk-components/src/utils/is-mobile';

export interface IHeroBannerProps extends IBaseElementProps {
	preHeading?: string;
	heading?: string;
	background?: BgColor;
	link?: ILink;
	image?: IImageProps;
	backgroundImage?: IImage;
	backgroundVideo?: IVideo;
	backgroundVideoPlayLabel?: string;
	backgroundVideoPauseLabel?: string;
	contentClassName?: string;
	imageClassName?: string;
	videoClassName?: string;
	children?: any;
	integrateWithNavbar?: boolean;
	useImageOverlay?: boolean;
	video?: IVideoProps;
	/**
	 * Content Alignment
	 */
	contentAlignment?: 'left' | 'center' | 'right';
}

const ContentHeroBanner: React.FC<IHeroBannerProps> = ({
	background = 'dark',
	preHeading,
	heading,
	className,
	classNames = [],
	children,
	image,
	backgroundImage,
	backgroundVideo,
	backgroundVideoPlayLabel = 'Play',
	backgroundVideoPauseLabel = 'Pause',
	link,
	contentClassName,
	imageClassName,
	videoClassName,
	contentAlignment = 'left',
	integrateWithNavbar = false,
	useImageOverlay = false,
	video,
	...props
}) => {
	const bgVideoRef = useRef(null);
	const bgVideoBtnRef = useRef(null);
	const autoPlay = !isMobile() && video && video.autoPlay;

	useEffect(() => {
		if (integrateWithNavbar === true) {
			const navbar = document.querySelector('.navbar.navbar-main');
			navbar && navbar.classList.add('with-hero-banner');
		}
	}, []);

	// Background video playback
	useEffect(() => {
		if (!bgVideoRef.current || !bgVideoBtnRef.current) {
			return;
		}

		const video = bgVideoRef.current as HTMLVideoElement;
		const btn = bgVideoBtnRef.current as HTMLVideoElement;

		btn.innerText = autoPlay
			? backgroundVideoPauseLabel
			: backgroundVideoPlayLabel;

		const onPlay = () => {
			btn.innerText = backgroundVideoPauseLabel;
			btn.classList.add('btn-pause');
		};

		const onPause = () => {
			btn.innerText = backgroundVideoPlayLabel;
			btn.classList.remove('btn-pause');
		};

		video.addEventListener('play', onPlay);
		video.addEventListener('pause', onPause);

		return () => {
			video.removeEventListener('play', onPlay);
			video.removeEventListener('pause', onPause);
		};
	}, []);

	return (
		<div
			className={
				className ||
				cn(
					'hero-banner',
					`hero-banner-align-${contentAlignment}`,
					`bg-${background}`,
					`text-${background === 'dark' ? 'light' : 'dark'}`,
					image && 'hero-banner--image',
					video && 'hero-banner--video',
					backgroundVideo && 'hero-banner--bg-video',
					...classNames,
				)
			}
			style={
				backgroundImage
					? {
							backgroundImage: `url("${backgroundImage.src}")`,
					  }
					: {}
			}
			{...props}
		>
			{useImageOverlay === true &&
				(backgroundImage || backgroundVideo) && (
					<div
						className={cn(
							'hero-banner-overlay',
							`bg-${background}`,
						)}
					/>
				)}

			{backgroundVideo && (
				<>
					<video
						src={backgroundVideo.src}
						className="hero-banner__bg-video"
						autoPlay
						muted
						loop
						ref={bgVideoRef}
					/>

					<button
						className="hero-banner__bg-video-btn"
						ref={bgVideoBtnRef}
						onClick={() => {
							const video = bgVideoRef.current! as HTMLVideoElement;
							video.paused ? video.play() : video.pause();
						}}
					>
						{backgroundVideoPauseLabel}
					</button>
				</>
			)}

			{(image && !video?.poster) && (
				<div
					className={
						imageClassName ||
						cn('hero-banner-image', 'mb-3', 'mb-sm-0')
					}
				>
					<Image {...image} />
				</div>
			)}

			{video?.poster && (
				<div
					className={
						videoClassName ||
						cn('hero-banner-video', 'col-sm-6')
					}
				>
					<Video
						{...video}
						autoPlay={!isMobile() && video.autoPlay}
					/>
				</div>
			)}

			<div className="container">
				<div
					className={cn(
						'row',
						'align-items-center',
						contentAlignment === 'center' &&
							'justify-content-center text-center',
						contentAlignment === 'right' && 'justify-content-end',
					)}
				>
					<div
						className={
							contentClassName ||
							cn('hero-banner-content', 'col-lg-6')
						}
					>
						{preHeading && (
							<h2 className="hero-banner-pre-heading">
								{preHeading}
							</h2>
						)}

						{heading && (
							<h1 className="hero-banner-heading">{heading}</h1>
						)}

						{children}

						{link && (
							<div>
								<Button
									type={
										background === 'dark'
											? 'light'
											: 'primary'
									}
									classNames={['mt-3']}
									{...link}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ContentHeroBanner;
